import React, { useState, useEffect } from 'react';
import {
  Building2,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Users,
  Database,
  Award,
  Loader2,
  AlertCircle,
  Minus,
  ArrowUpRight,
  ArrowDownRight,
  ShieldAlert,
  Sparkles,
  RefreshCw,
  Info,
  Briefcase,
  BookOpen,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import logger from '../lib/logger';
import { useOrgAnonymizer } from '../hooks/useOrgAnonymizer';
import { OrgFilterBar } from '../components/benchmarking/OrgFilterBar';
import { FilterEmptyState } from '../components/benchmarking/FilterEmptyState';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  LineChart as RechartsLineChart,
  Line,
  ReferenceLine
} from 'recharts';

import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
const PILLAR_COLORS = {
  'P1 Access & Engagement': '#0d9488',
  'P2 Quality & Effectiveness': '#6366f1',
  'P3 Skills & Capability': '#f59e0b',
  'P4 Efficiency & ROI': '#ec4899'
};

// Missing Middle reframe: each LEI pillar maps to a Navigation
// Condition — the underlying organisational capability that
// determines how well an org converts learning into value transfer.
// Used in tooltips, insight cards, and the Navigation Comparison
// tab. The original pillar labels stay on the chart legend/axis
// for continuity, but the strategic framing lives here.
const NAVIGATION_CONDITION_BY_PILLAR = {
  'P1 Access & Engagement': 'Visibility Conditions',
  'P2 Quality & Effectiveness': 'Confidence Conditions',
  'P3 Skills & Capability': 'Capability Conditions',
  'P4 Efficiency & ROI': 'Coordination Conditions',
};

// Pillar key → short label without the "Pn " prefix (used as a
// fallback display label everywhere the Navigation Condition isn't
// the primary surface).
const pillarShortLabel = (p) => (p || '').replace(/^P\d\s+/, '');

const navConditionFor = (pillarKey) =>
  NAVIGATION_CONDITION_BY_PILLAR[pillarKey] || pillarShortLabel(pillarKey);

// Navigation Position thresholds — bucket each org by its
// overall_score quartile rank inside the filtered cohort. Returns
// {label, tone} where tone drives the chip palette in the
// Navigation Comparison tab.
function getNavigationPosition(orgScore, allScores) {
  if (!orgScore || !allScores?.length) {
    return { label: 'No data', tone: 'slate' };
  }
  const sorted = [...allScores].filter((s) => s > 0).sort((a, b) => a - b);
  if (!sorted.length) return { label: 'No data', tone: 'slate' };
  const n = sorted.length;
  const p75 = sorted[Math.floor(n * 0.75)];
  const p50 = sorted[Math.floor(n * 0.50)];
  const p25 = sorted[Math.floor(n * 0.25)];
  if (orgScore >= p75) return { label: 'Leader', tone: 'emerald' };
  if (orgScore >= p50) return { label: 'Strong', tone: 'blue' };
  if (orgScore >= p25) return { label: 'Emerging', tone: 'amber' };
  return { label: 'Developing', tone: 'rose' };
}

const ORG_COLORS = [
  '#0d9488', '#6366f1', '#f59e0b', '#ec4899', '#3b82f6',
  '#10b981', '#8b5cf6', '#f97316', '#ef4444', '#06b6d4'
];

function ScoreBadge({ score }) {
  if (score === null || score === undefined) {
    return <Badge variant="outline" className="text-slate-400 border-slate-300">No data</Badge>;
  }
  if (score >= 75) return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">{score.toFixed(1)}</Badge>;
  if (score >= 50) return <Badge className="bg-amber-100 text-amber-800 border-amber-200">{score.toFixed(1)}</Badge>;
  return <Badge className="bg-rose-100 text-rose-800 border-rose-200">{score.toFixed(1)}</Badge>;
}

function RankBadge({ rank }) {
  if (rank === 1) return <span className="inline-flex items-center gap-1 text-amber-600 font-bold"><Award className="h-4 w-4" /> #{rank}</span>;
  return <span className="text-slate-600 font-medium">#{rank}</span>;
}

export default function OrgBenchmarksPage() {
  const { isSuperAdmin, user, viewAsOrg } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [leiData, setLeiData] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [leiTrends, setLeiTrends] = useState(null);
  const [kpiTrends, setKpiTrends] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPillar, setSelectedPillar] = useState('overall');
  const [selectedKpiTrend, setSelectedKpiTrend] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  // Industry filter on the LEI Pillar Comparison chart. Pulls
  // `industry_id` per org from `/rbac/organizations` and the
  // canonical industry list from `/benchmarking/industries`.
  const [orgIndustryMap, setOrgIndustryMap] = useState({});
  const [industries, setIndustries] = useState([]);
  const [pillarChartIndustry, setPillarChartIndustry] = useState('all');
  // Region filter — region per org also rides on /rbac/organizations.
  // Region is a free-text profile field today, so we build the set
  // of available values from the live data rather than a static
  // catalog.
  const [orgRegionMap, setOrgRegionMap] = useState({});
  const [regionFilter, setRegionFilter] = useState('all');
  // Company size filter — values are slugs like "small" / "medium" /
  // "large" / "enterprise" stored in the org profile.
  const [orgSizeMap, setOrgSizeMap] = useState({});
  const [sizeFilter, setSizeFilter] = useState('all');

  // Scoped org for "(You)" highlighting + anonymization decisions.
  const scopedOrgId = (viewAsOrg && viewAsOrg.id) || user?.organization_id || null;
  // Preview-mode super admin sees the world through a single org's
  // lens — that's the same visibility a regular user would have, so
  // we anonymize peers while keeping their own org's name visible.
  const shouldAnonymize = !isSuperAdmin || !!viewAsOrg;

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchAll();
  }, [isSuperAdmin]);

  // Pull the org → industry/region map + industry list once for
  // the filters.
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const [orgsRes, indRes] = await Promise.all([
          api.get('/rbac/organizations'),
          api.get('/benchmarking/industries'),
        ]);
        const indMap = {};
        const regMap = {};
        const sizeMap = {};
        (orgsRes.data?.organizations || []).forEach(o => {
          // The rbac payload exposes `industry_type` (a slug like
          // "technology"), which matches the `id` field of items
          // returned by /benchmarking/industries — so we can use it
          // directly as our industry key.
          if (o.industry_type) indMap[o.id] = o.industry_type;
          if (o.region) regMap[o.id] = o.region;
          if (o.company_size) sizeMap[o.id] = o.company_size;
        });
        setOrgIndustryMap(indMap);
        setOrgRegionMap(regMap);
        setOrgSizeMap(sizeMap);
        setIndustries(indRes.data?.industries || []);
      } catch {
        /* non-fatal — chart just shows "all" */
      }
    })();
  }, [isSuperAdmin]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [overviewRes, leiRes, kpiRes, leiTrendsRes, kpiTrendsRes] = await Promise.all([
        api.get('/benchmarking/cross-org/overview'),
        api.get('/benchmarking/cross-org/lei-summary'),
        api.get('/benchmarking/cross-org/kpi-summary'),
        api.get('/benchmarking/cross-org/lei-trends'),
        api.get('/benchmarking/cross-org/kpi-trends')
      ]);
      setOverview(overviewRes.data);
      setLeiData(leiRes.data);
      setKpiData(kpiRes.data);
      setLeiTrends(leiTrendsRes.data);
      setKpiTrends(kpiTrendsRes.data);
      // Auto-select first KPI for trends
      const kpiIds = Object.keys(kpiTrendsRes.data?.kpi_trends || {});
      if (kpiIds.length > 0) setSelectedKpiTrend(kpiIds[0]);
    } catch (error) {
      logger.error('Failed to fetch cross-org benchmarks:', error);
      toast.error('Failed to load organization benchmarks');
    }
    setLoading(false);
  };

  const generateInsights = async () => {
    setInsightsLoading(true);
    try {
      const res = await api.post('/benchmarking/cross-org/ai-insights');
      setAiInsights(res.data);
      toast.success('AI insights generated');
    } catch (error) {
      logger.error('Failed to generate insights:', error);
      toast.error('Failed to generate AI insights');
    }
    setInsightsLoading(false);
  };

  // Centralized RBAC anonymization + filter predicate + chip renderers.
  // Must be called before any early returns so the hook-call order is
  // stable across renders. The hook is given the union of orgs across
  // all payload sources (overview + lei) so peer-letter assignment
  // stays consistent even when one slice is missing an org.
  const _mergedOrgs = [
    ...(overview?.organizations || []),
    ...((leiData?.organizations) || []),
  ];
  const {
    orgLabelById,
    industryForOrg,
    regionForOrg,
    sizeForOrg,
    matchesFilters: _matchesFilters,
    availableRegions,
    availableSizes,
    renderOrgLabel,
    anonymizeNarrative,
  } = useOrgAnonymizer({
    isSuperAdmin,
    viewAsOrg,
    scopedOrgId,
    orgs: _mergedOrgs,
    orgIndustryMap,
    orgRegionMap,
    orgSizeMap,
    industries,
    filters: { industry: pillarChartIndustry, region: regionFilter, size: sizeFilter },
  });

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center" data-testid="org-benchmarks-access-denied">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <ShieldAlert className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Access Restricted</h2>
            <p className="text-slate-500">Cross-organization benchmarking is available to Super Admins only.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center" data-testid="org-benchmarks-loading">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const pillarNames = leiData?.pillar_names?.filter(Boolean) || [];
  const leiOrgs = leiData?.organizations || [];
  const kpiOrgs = kpiData?.organizations || [];
  const kpiRankings = kpiData?.kpi_rankings || {};

  // Prepare bar chart data for LEI pillar comparison
  const pillarChartData = pillarNames.map(pillar => {
    const entry = { pillar: pillar.replace('P1 ', '').replace('P2 ', '').replace('P3 ', '').replace('P4 ', '') };
    leiOrgs.forEach((org, i) => {
      entry[org.organization_name] = org.pillars?.[pillar]?.avg_score || 0;
    });
    return entry;
  });

  // Prepare radar chart data
  const radarData = pillarNames.map(pillar => {
    const entry = { pillar: pillar.replace(/^P\d\s+/, '') };
    leiOrgs.forEach(org => {
      entry[org.organization_name] = org.pillars?.[pillar]?.avg_score || 0;
    });
    return entry;
  });

  // Filtered views — overview + LEI org lists are narrowed by the
  // hook's matchesFilters predicate so every surface stays in sync
  // with the Industry/Region/Size filter pills.
  const overviewOrgsRaw = overview?.organizations || [];
  const filteredOverviewOrgs = overviewOrgsRaw.filter((o) => _matchesFilters(o.organization_id));
  const filteredLeiOrgs = leiOrgs.filter((o) => _matchesFilters(o.organization_id));
  const isOrgInFilter = (orgId) => _matchesFilters(orgId);

  // Per-org row with all pillar scores as keys — feeds the new
  // horizontal stacked bar chart on the Overview tab. Sorted by
  // overall_score so the strongest org sits at the top. Each
  // pillar segment is divided by the pillar count so the stack
  // sums to the org's overall LEI (0-100 indexed) — matches how
  // overall_score is computed (mean of pillar averages).
  const _pillarN = Math.max(1, pillarNames.length);
  const orgPillarStackData = [...filteredLeiOrgs]
    .sort((a, b) => (b.overall_score || 0) - (a.overall_score || 0))
    .map((org) => {
      const row = {
        organization_id: org.organization_id,
        organization_name: orgLabelById[org.organization_id] || org.organization_name,
        _is_you: org.organization_id === scopedOrgId,
        _overall: org.overall_score || 0,
      };
      pillarNames.forEach((p) => {
        const raw = org.pillars?.[p]?.avg_score || 0;
        row[p] = +(raw / _pillarN).toFixed(2);
        row[`${p}__raw`] = raw;
      });
      return row;
    });

  // Pillar-level insights for the side callout panel — strongest
  // pillar overall, biggest spread between orgs, etc.
  const pillarInsights = pillarNames.map((p) => {
    const scores = filteredLeiOrgs
      .map((org) => ({
        id: org.organization_id,
        name: orgLabelById[org.organization_id] || org.organization_name,
        score: org.pillars?.[p]?.avg_score || 0,
      }))
      .filter((r) => r.score > 0);
    if (!scores.length) return null;
    const sorted = [...scores].sort((a, b) => b.score - a.score);
    const top = sorted[0];
    const bottom = sorted[sorted.length - 1];
    const avg = scores.reduce((s, r) => s + r.score, 0) / scores.length;
    return {
      pillar: p,
      label: p.replace(/^P\d\s+/, ''),
      avg,
      top,
      spread: top.score - bottom.score,
      bottom,
    };
  }).filter(Boolean);
  const strongestPillar = [...pillarInsights].sort((a, b) => b.avg - a.avg)[0];
  const widestGapPillar = [...pillarInsights].sort((a, b) => b.spread - a.spread)[0];

  // ─── Navigation Insights (deterministic narrative built from existing data) ──
  // Surfaces: leading navigator + your-org's strongest/weakest condition +
  // the move-to-top-quartile bridge. No LLM call; data is already on hand.
  const filteredScoresArr = filteredLeiOrgs
    .map((o) => o.overall_score || 0)
    .filter((s) => s > 0);
  const leadingNavigator = [...filteredLeiOrgs]
    .sort((a, b) => (b.overall_score || 0) - (a.overall_score || 0))[0];
  const youOrg = filteredLeiOrgs.find((o) => o.organization_id === scopedOrgId);
  let yourStrongest = null;
  let yourWeakest = null;
  let yourPositionLabel = null;
  let topQuartileLei = null;
  if (youOrg && pillarNames.length) {
    const yourPillarScores = pillarNames
      .map((p) => ({
        pillar: p,
        label: pillarShortLabel(p),
        navCondition: navConditionFor(p),
        score: youOrg.pillars?.[p]?.avg_score || 0,
      }))
      .filter((r) => r.score > 0);
    if (yourPillarScores.length) {
      yourStrongest = [...yourPillarScores].sort((a, b) => b.score - a.score)[0];
      yourWeakest = [...yourPillarScores].sort((a, b) => a.score - b.score)[0];
    }
    yourPositionLabel = getNavigationPosition(youOrg.overall_score || 0, filteredScoresArr);
    if (filteredScoresArr.length) {
      const sorted = [...filteredScoresArr].sort((a, b) => a - b);
      topQuartileLei = sorted[Math.floor(sorted.length * 0.75)];
    }
  }

  // ─── Top Navigators (top-quartile) vs overall-median deltas ───────────────
  // Per Navigation Condition + a probability-of-positive-ROI line if
  // overview.kpi_rankings carries it. Used by the "What Top Navigators
  // Do Differently" card on the Overview tab.
  const topNavigatorDeltas = pillarNames
    .map((p) => {
      const scores = filteredLeiOrgs
        .map((o) => o.pillars?.[p]?.avg_score || 0)
        .filter((s) => s > 0);
      if (scores.length < 2) return null;
      const sorted = [...scores].sort((a, b) => a - b);
      const median = sorted[Math.floor(sorted.length / 2)];
      const tqStart = Math.floor(sorted.length * 0.75);
      const tqSlice = sorted.slice(tqStart);
      const tqAvg = tqSlice.reduce((s, v) => s + v, 0) / Math.max(1, tqSlice.length);
      if (!median || tqAvg <= median) return null;
      const deltaPct = ((tqAvg - median) / median) * 100;
      return {
        pillar: p,
        label: navConditionFor(p),
        pillarLabel: pillarShortLabel(p),
        median: +median.toFixed(1),
        topQuartile: +tqAvg.toFixed(1),
        deltaPct: +deltaPct.toFixed(0),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.deltaPct - a.deltaPct);

  const filteredKpiOrgs = kpiOrgs.filter(o => isOrgInFilter(o.organization_id));

  const _filtersActive = pillarChartIndustry !== 'all' || regionFilter !== 'all' || sizeFilter !== 'all';
  const _clearAllFilters = () => {
    setPillarChartIndustry('all');
    setRegionFilter('all');
    setSizeFilter('all');
  };
  // Reusable inline "Clear filters" link shown under in-card empty
  // states, so users don't have to scroll back up to the filter bar.
  const ClearFiltersLink = ({ testId }) =>
    _filtersActive ? (
      <button
        type="button"
        onClick={_clearAllFilters}
        className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 underline"
        data-testid={testId}
      >
        Clear filters
      </button>
    ) : null;
  const filteredKpiRankings = Object.fromEntries(
    Object.entries(kpiRankings).map(([kid, kdata]) => [kid, {
      ...kdata,
      rankings: (kdata.rankings || []).filter(r => isOrgInFilter(r.organization_id)),
    }])
  );
  const filteredOrgTrends = ((leiTrends?.org_trends) || []).filter(o => isOrgInFilter(o.organization_id));

  const __pageTheme = getLightSectionTheme('Navy');
  // Mirror Industry Benchmarks hero shape: full PageHero with the
  // Building2 icon and a 4-up KPI strip summarising the cross-org
  // dataset at a glance.
  const orgs = overview?.organizations || [];
  const totalOrgs = orgs.length;
  const orgsWithData = orgs.filter(o => o.has_data).length;
  const totalUsers = orgs.reduce((s, o) => s + (o.user_count || 0), 0);
  const totalLeiPoints = orgs.reduce((s, o) => s + (o.lei_data_points || 0), 0);
  const orgBenchmarkHeroStats = [
    { label: 'Navigation Network', value: `${totalOrgs} Organizations`, testId: 'org-benchmarks-stat-total-orgs' },
    { label: 'Active Comparisons', value: `${orgsWithData} With Data`, testId: 'org-benchmarks-stat-with-data' },
    { label: 'Community Signals', value: `${totalUsers.toLocaleString()} Users`, testId: 'org-benchmarks-stat-users' },
    { label: 'Evidence Base', value: `${totalLeiPoints.toLocaleString()} Data Points`, testId: 'org-benchmarks-stat-lei-points' },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Organization Benchmarks"
        description="Compare how organisations navigate the Missing Middle — converting capability into value transfer — across peers. Evidence → Insight → Navigation."
        theme={__pageTheme}
        icon={Building2}
        stats={orgBenchmarkHeroStats}
        actions={(
          <Link
            to="/benchmarks-reference"
            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-md transition-colors"
            data-testid="org-benchmarks-references-link"
          >
            <BookOpen className="h-3.5 w-3.5" />
            References
          </Link>
        )}
        testId="org-benchmarks-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6" data-testid="org-benchmarks-page">
        {/* Header */}
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <TabsList className="bg-white border shadow-sm">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="navigation" data-testid="tab-navigation">Navigation Comparison</TabsTrigger>
              <TabsTrigger value="lei" data-testid="tab-lei">LEI Comparison</TabsTrigger>
              <TabsTrigger value="kpi" data-testid="tab-kpi">KPI Comparison</TabsTrigger>
              <TabsTrigger value="trends" data-testid="tab-trends">Trends</TabsTrigger>
            </TabsList>
            {/* Page-level industry + region filters — scope every tab. */}
            <OrgFilterBar
              industries={industries}
              availableRegions={availableRegions}
              availableSizes={availableSizes}
              industryValue={pillarChartIndustry}
              onIndustryChange={setPillarChartIndustry}
              regionValue={regionFilter}
              onRegionChange={setRegionFilter}
              sizeValue={sizeFilter}
              onSizeChange={setSizeFilter}
              testIdPrefix="benchmark"
            />
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Org Cards */}
            {filteredOverviewOrgs.length === 0 ? (
              <FilterEmptyState
                onClear={() => { setPillarChartIndustry('all'); setRegionFilter('all'); setSizeFilter('all'); }}
                testId="overview-filter-empty"
              />
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredOverviewOrgs.map((org, i) => {
                const isYou = org.organization_id === scopedOrgId;
                const displayName = orgLabelById[org.organization_id] || org.organization_name;
                const indName = industryForOrg(org.organization_id);
                const regName = regionForOrg(org.organization_id);
                const szName = sizeForOrg(org.organization_id);
                return (
                <Card
                  key={org.organization_id}
                  className={`border-l-4 ${isYou ? 'ring-2 ring-blue-300' : ''}`}
                  style={{ borderLeftColor: ORG_COLORS[i % ORG_COLORS.length] }}
                  data-testid={`org-card-${org.organization_id}`}
                >
                  <CardContent className="py-5">
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Building2 className="h-5 w-5" style={{ color: ORG_COLORS[i % ORG_COLORS.length] }} />
                        <h3 className={`font-semibold ${isYou ? 'text-blue-700' : 'text-slate-800'}`}>{displayName}</h3>
                        {indName && (
                          <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                            {indName}
                          </span>
                        )}
                        {regName && (
                          <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                            {regName}
                          </span>
                        )}
                        {szName && (
                          <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                            {szName}
                          </span>
                        )}
                      </div>
                      {org.has_data ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400">No Data</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="bg-slate-50 rounded p-2 text-center">
                        <p className="text-slate-500">Company Size</p>
                        <p className="font-bold text-slate-800 flex items-center justify-center gap-1">
                          <Briefcase className="h-3.5 w-3.5" />
                          {sizeForOrg(org.organization_id) || '—'}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2 text-center">
                        <p className="text-slate-500 inline-flex items-center justify-center gap-1">
                          LEI Points
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="text-slate-400 hover:text-slate-700 transition-colors"
                                aria-label="What is an LEI Point?"
                                data-testid={`lei-points-info-${org.organization_id}`}
                              >
                                <Info className="h-3 w-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 text-xs space-y-2 text-slate-700" side="top">
                              <p className="font-semibold text-sm text-slate-900">LEI Points</p>
                              <p>
                                Each <strong>LEI Point</strong> is one measurement of a Learning
                                Effectiveness Index measure (e.g. <em>Training Performance Index</em>,
                                <em> Net Promoter Score</em>) for a given segment + period.
                              </p>
                              <p>
                                Sourced from the org's LEI baselines and any quarterly snapshots
                                that feed <code>db.lei_baselines</code>. More points = denser
                                history → more reliable trend lines + benchmark percentiles.
                              </p>
                              <p className="text-slate-500">
                                Read this as breadth of LEI coverage. <strong>Higher is better</strong>.
                              </p>
                            </PopoverContent>
                          </Popover>
                        </p>
                        <p className="font-bold text-slate-800 flex items-center justify-center gap-1"><BarChart3 className="h-3.5 w-3.5" />{org.lei_data_points}</p>
                      </div>
                      <div className="bg-slate-50 rounded p-2 text-center">
                        <p className="text-slate-500 inline-flex items-center justify-center gap-1">
                          KPI Points
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="text-slate-400 hover:text-slate-700 transition-colors"
                                aria-label="What is a KPI Point?"
                                data-testid={`kpi-points-info-${org.organization_id}`}
                              >
                                <Info className="h-3 w-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 text-xs space-y-2 text-slate-700" side="top">
                              <p className="font-semibold text-sm text-slate-900">KPI Points</p>
                              <p>
                                Each <strong>KPI Point</strong> is a recorded business KPI actual
                                (e.g. <em>Revenue per FTE</em>, <em>Quality Score</em>, <em>Retention %</em>)
                                for a period. These are the outcomes the org's L&D programs are
                                ultimately judged against.
                              </p>
                              <p>
                                Sourced from the KPI ingestion pipeline and stored in
                                <code> db.kpi_values</code>. More points across more periods = stronger
                                pre/post comparisons and ROI attribution.
                              </p>
                              <p className="text-slate-500">
                                Read this as breadth of business-outcome coverage.
                                <strong> Higher is better</strong>.
                              </p>
                            </PopoverContent>
                          </Popover>
                        </p>
                        <p className="font-bold text-slate-800 flex items-center justify-center gap-1"><Database className="h-3.5 w-3.5" />{org.kpi_data_points}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
            )}

            {/* Navigation Insights — small AI-style summary panel ABOVE
                the chart. Built deterministically from existing
                overview/lei data: leading navigator + your strongest/
                weakest condition + move-to-top-quartile bridge. */}
            {(leadingNavigator || (youOrg && yourStrongest)) && (
              <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-violet-50/40" data-testid="org-bench-navigation-insights">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-indigo-600" />
                    Navigation Insights
                  </CardTitle>
                  <p className="text-xs text-slate-500">
                    How peer organisations differ in their ability to convert capability into value.
                  </p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {leadingNavigator && (
                      <li className="flex gap-2" data-testid="nav-insight-leader">
                        <span className="text-indigo-500 mt-0.5">▸</span>
                        <span>
                          <strong>{orgLabelById[leadingNavigator.organization_id] || leadingNavigator.organization_name}</strong>{' '}
                          demonstrates the strongest overall navigation conditions
                          {leadingNavigator.overall_score
                            ? ` (overall ${leadingNavigator.overall_score.toFixed(1)})`
                            : ''}.
                        </span>
                      </li>
                    )}
                    {youOrg && yourStrongest && yourWeakest && yourStrongest.pillar !== yourWeakest.pillar && (
                      <li className="flex gap-2" data-testid="nav-insight-strength-weakness">
                        <span className="text-indigo-500 mt-0.5">▸</span>
                        <span>
                          <strong>{orgLabelById[youOrg.organization_id] || youOrg.organization_name}</strong>{' '}
                          performs strongly in <strong>{yourStrongest.navCondition}</strong>{' '}
                          ({yourStrongest.score.toFixed(1)}) but trails peers in{' '}
                          <strong>{yourWeakest.navCondition}</strong> ({yourWeakest.score.toFixed(1)}).
                        </span>
                      </li>
                    )}
                    {youOrg && yourWeakest && topQuartileLei && (youOrg.overall_score || 0) < topQuartileLei && (
                      <li className="flex gap-2" data-testid="nav-insight-stretch">
                        <span className="text-indigo-500 mt-0.5">▸</span>
                        <span>
                          Improving <strong>{yourWeakest.navCondition.toLowerCase()}</strong> by{' '}
                          <strong>{Math.max(1, Math.round(topQuartileLei - (youOrg.overall_score || 0)))} points</strong>{' '}
                          would move {orgLabelById[youOrg.organization_id] || youOrg.organization_name} into the top quartile of peer organisations.
                        </span>
                      </li>
                    )}
                    {youOrg && yourPositionLabel && yourPositionLabel.label !== 'No data' && (
                      <li className="flex gap-2 text-xs text-slate-500" data-testid="nav-insight-position">
                        <span className="opacity-60 mt-0.5">·</span>
                        <span>
                          Current position: <strong className="text-slate-700">{yourPositionLabel.label}</strong> navigator within the filtered cohort.
                        </span>
                      </li>
                    )}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Navigation Conditions Comparison — horizontal stacked
                bars with insights panel. Replaces the radar so the
                wide canvas actually does work for us. Each row = one
                org, segments stack the four pillar scores so the
                SHAPE of each org's navigation footprint is comparable
                side-by-side. */}
            {orgPillarStackData.length > 0 && pillarNames.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Navigation Conditions Comparison</CardTitle>
                  <p className="text-xs text-slate-500">Compare the organisational conditions that influence value transfer across peer organisations. Each segment = condition score ÷ condition count, so stacks sum to the org&apos;s overall navigation index on a 0–100 scale.</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-8">
                      {orgPillarStackData.length === 0 ? (
                        <div className="h-64 flex items-center justify-center text-sm text-slate-400 italic" data-testid="pillar-chart-empty">
                          No organizations match this industry filter.
                        </div>
                      ) : (
                      <div style={{ height: Math.max(260, orgPillarStackData.length * 56) }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={orgPillarStackData}
                            layout="vertical"
                            margin={{ top: 10, right: 24, left: 8, bottom: 10 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                            <XAxis
                              type="number"
                              domain={[0, 100]}
                              ticks={[0, 25, 50, 75, 100]}
                              tick={{ fontSize: 11, fill: '#64748b' }}
                            />
                            <YAxis
                              type="category"
                              dataKey="organization_name"
                              tick={({ x, y, payload }) => {
                                const row = orgPillarStackData.find(r => r.organization_name === payload.value);
                                const isYou = row?._is_you;
                                return (
                                  <text
                                    x={x}
                                    y={y}
                                    dy={4}
                                    textAnchor="end"
                                    fontSize={12}
                                    fontWeight={isYou ? 800 : 600}
                                    fill={isYou ? '#1d4ed8' : '#1e293b'}
                                  >
                                    {payload.value}
                                  </text>
                                );
                              }}
                              width={170}
                            />
                            <Tooltip
                              cursor={{ fill: '#f1f5f9' }}
                              formatter={(value, name, props) => {
                                const raw = props?.payload?.[`${name}__raw`];
                                const pillarLabel = pillarShortLabel(name);
                                const navLabel = navConditionFor(name);
                                return [
                                  `${Number(raw ?? 0).toFixed(1)} (contributes ${Number(value).toFixed(1)})`,
                                  navLabel === pillarLabel ? pillarLabel : `${navLabel} · ${pillarLabel}`,
                                ];
                              }}
                            />
                            <Legend
                              verticalAlign="bottom"
                              wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                              formatter={(v) => pillarShortLabel(v)}
                            />
                            {pillarNames.map(p => (
                              <Bar
                                key={p}
                                dataKey={p}
                                stackId="pillars"
                                fill={PILLAR_COLORS[p] || '#94a3b8'}
                              />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      )}
                    </div>

                    {/* Insights panel — uses the slack to surface what
                        the chart only implies. */}
                    <div className="lg:col-span-4 space-y-3">
                      {strongestPillar && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="org-bench-insight-strongest">
                          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1">
                            <TrendingUp size={12} /> Strongest Navigation Condition
                          </div>
                          <div className="text-base font-bold text-slate-900">
                            {navConditionFor(strongestPillar.pillar)}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5">
                            Avg <strong>{strongestPillar.avg.toFixed(1)}</strong> across {leiOrgs.length} orgs · led by <strong>{strongestPillar.top.name}</strong> at {strongestPillar.top.score.toFixed(1)}
                          </div>
                        </div>
                      )}
                      {widestGapPillar && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="org-bench-insight-gap">
                          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-1">
                            <ShieldAlert size={12} /> Largest Competitive Gap
                          </div>
                          <div className="text-base font-bold text-slate-900">
                            {navConditionFor(widestGapPillar.pillar)}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5">
                            <strong>{widestGapPillar.spread.toFixed(1)}-pt gap</strong> · {widestGapPillar.top.name} ({widestGapPillar.top.score.toFixed(1)}) vs {widestGapPillar.bottom.name} ({widestGapPillar.bottom.score.toFixed(1)})
                          </div>
                        </div>
                      )}
                      {orgPillarStackData[0] && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="org-bench-insight-leader">
                          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-blue-700 mb-1">
                            <Award size={12} /> Leading Navigator
                          </div>
                          <div className="text-base font-bold text-slate-900">
                            {orgPillarStackData[0].organization_name}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5">
                            Overall navigation index <strong>{(orgPillarStackData[0]._overall || 0).toFixed(1)}</strong>
                            {orgPillarStackData[1]
                              ? ` · ${(orgPillarStackData[0]._overall - (orgPillarStackData[1]._overall || 0)).toFixed(1)} pts ahead of ${orgPillarStackData[1].organization_name}`
                              : ''}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* What Top Navigators Do Differently — top-quartile vs
                overall-median deltas per Navigation Condition. Direct
                bridge to Solution Finder / Impact Modelling / Mission
                Control so users know what to improve. */}
            {topNavigatorDeltas.length > 0 && (
              <Card className="border-teal-200 bg-gradient-to-br from-teal-50/60 to-emerald-50/40" data-testid="org-bench-top-navigators-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Award className="h-4 w-4 text-teal-600" />
                    What Top Navigators Do Differently
                  </CardTitle>
                  <p className="text-xs text-slate-500">
                    Top-quartile peers vs the overall median across this cohort.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {topNavigatorDeltas.map((d) => {
                      // Deep-link to Solution Finder with navigation-condition
                      // context so it can surface a contextual banner.
                      const sfHref = `/solution-finder?focus=${encodeURIComponent(d.pillar)}&gap=${d.deltaPct}&label=${encodeURIComponent(d.label)}`;
                      return (
                        <Link
                          key={d.pillar}
                          to={sfHref}
                          className="rounded-lg border border-teal-200 bg-white px-3 py-2 hover:border-teal-400 hover:shadow-sm transition-all group"
                          data-testid={`top-navigator-delta-${d.pillar.replace(/\s+/g, '-')}`}
                        >
                          <div className="text-[10px] uppercase tracking-wider font-bold text-teal-700">
                            {d.label}
                          </div>
                          <div className="text-xl font-bold text-slate-900 mt-1">
                            +{d.deltaPct}%
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {d.topQuartile} vs median {d.median}
                          </div>
                          <div className="mt-1.5 text-[10px] font-semibold text-teal-700 opacity-0 group-hover:opacity-100 transition-opacity">
                            Close this gap →
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Link
                      to={topNavigatorDeltas[0]
                        ? `/solution-finder?focus=${encodeURIComponent(topNavigatorDeltas[0].pillar)}&gap=${topNavigatorDeltas[0].deltaPct}&label=${encodeURIComponent(topNavigatorDeltas[0].label)}`
                        : '/solution-finder'}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-md transition-colors"
                      data-testid="top-navigator-cta-solution-finder"
                    >
                      {topNavigatorDeltas[0]
                        ? `Close the ${topNavigatorDeltas[0].label} gap →`
                        : 'Find a solution →'}
                    </Link>
                    <Link
                      to="/impact-modelling"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-teal-300 hover:bg-teal-50 text-teal-700 text-xs font-semibold rounded-md transition-colors"
                      data-testid="top-navigator-cta-impact-modelling"
                    >
                      Model the impact
                    </Link>
                    <Link
                      to="/mission-control"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-teal-300 hover:bg-teal-50 text-teal-700 text-xs font-semibold rounded-md transition-colors"
                      data-testid="top-navigator-cta-mission-control"
                    >
                      Open Mission Control
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Navigation Comparison Tab — synthesis view. One row per
              org with a quartile-bucketed position chip + their
              strongest/weakest navigation conditions + the
              largest-opportunity stretch. No new math; pure
              presentation of data the Overview tab already shows. */}
          <TabsContent value="navigation" className="space-y-6" data-testid="navigation-comparison-tab">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Navigation Comparison</CardTitle>
                <p className="text-xs text-slate-500">
                  Per-org navigation effectiveness — strongest condition, weakest condition, and the
                  largest competitive opportunity for each peer.
                </p>
              </CardHeader>
              <CardContent>
                {filteredLeiOrgs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500" data-testid="nav-comparison-empty">
                    No organizations match the current filters.
                    <ClearFiltersLink testId="nav-comparison-clear-filters" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="nav-comparison-table">
                      <thead>
                        <tr className="border-b border-slate-200 text-left">
                          <th className="py-2.5 px-3 font-semibold text-slate-600">Organization</th>
                          <th className="py-2.5 px-3 font-semibold text-slate-600">Navigation Position</th>
                          <th className="py-2.5 px-3 font-semibold text-slate-600">Strongest Condition</th>
                          <th className="py-2.5 px-3 font-semibold text-slate-600">Weakest Condition</th>
                          <th className="py-2.5 px-3 font-semibold text-slate-600">Largest Opportunity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...filteredLeiOrgs]
                          .sort((a, b) => (b.overall_score || 0) - (a.overall_score || 0))
                          .map((org) => {
                            const isYou = org.organization_id === scopedOrgId;
                            const orgName = orgLabelById[org.organization_id] || org.organization_name;
                            const pillarScores = pillarNames
                              .map((p) => ({
                                pillar: p,
                                navCondition: navConditionFor(p),
                                score: org.pillars?.[p]?.avg_score || 0,
                              }))
                              .filter((r) => r.score > 0);
                            const strongest = pillarScores.length
                              ? [...pillarScores].sort((a, b) => b.score - a.score)[0]
                              : null;
                            const weakest = pillarScores.length
                              ? [...pillarScores].sort((a, b) => a.score - b.score)[0]
                              : null;
                            // Largest opportunity = the pillar where THIS org is
                            // furthest below the cohort median for that pillar.
                            let largestOpportunity = null;
                            let largestGap = 0;
                            pillarNames.forEach((p) => {
                              const cohortScores = filteredLeiOrgs
                                .map((o) => o.pillars?.[p]?.avg_score || 0)
                                .filter((s) => s > 0);
                              if (cohortScores.length < 2) return;
                              const sorted = [...cohortScores].sort((a, b) => a - b);
                              const median = sorted[Math.floor(sorted.length / 2)];
                              const orgScore = org.pillars?.[p]?.avg_score || 0;
                              if (!orgScore) return;
                              const gap = median - orgScore;
                              if (gap > largestGap) {
                                largestGap = gap;
                                largestOpportunity = {
                                  pillar: p,
                                  navCondition: navConditionFor(p),
                                  gap,
                                  median,
                                  yours: orgScore,
                                };
                              }
                            });
                            const position = getNavigationPosition(
                              org.overall_score || 0,
                              filteredScoresArr,
                            );
                            const toneClass = {
                              emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                              blue: 'bg-blue-100 text-blue-800 border-blue-300',
                              amber: 'bg-amber-100 text-amber-800 border-amber-300',
                              rose: 'bg-rose-100 text-rose-800 border-rose-300',
                              slate: 'bg-slate-100 text-slate-700 border-slate-300',
                            }[position.tone] || 'bg-slate-100 text-slate-700 border-slate-300';
                            return (
                              <tr
                                key={org.organization_id}
                                className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${isYou ? 'bg-blue-50/40' : ''}`}
                                data-testid={`nav-comparison-row-${org.organization_id}`}
                              >
                                <td className="py-3 px-3">
                                  <div className={`font-semibold ${isYou ? 'text-blue-800' : 'text-slate-900'}`}>
                                    {orgName}{isYou ? ' (You)' : ''}
                                  </div>
                                  <div className="text-[11px] text-slate-500">
                                    Overall {(org.overall_score || 0).toFixed(1)}
                                  </div>
                                </td>
                                <td className="py-3 px-3">
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${toneClass}`}
                                    data-testid={`nav-comparison-position-${org.organization_id}`}
                                  >
                                    {position.label}
                                  </span>
                                </td>
                                <td className="py-3 px-3 text-xs">
                                  {strongest ? (
                                    <>
                                      <div className="font-semibold text-slate-800">{strongest.navCondition}</div>
                                      <div className="text-slate-500">{strongest.score.toFixed(1)}</div>
                                    </>
                                  ) : (
                                    <span className="text-slate-400 italic">—</span>
                                  )}
                                </td>
                                <td className="py-3 px-3 text-xs">
                                  {weakest && strongest && weakest.pillar !== strongest.pillar ? (
                                    <>
                                      <div className="font-semibold text-slate-800">{weakest.navCondition}</div>
                                      <div className="text-slate-500">{weakest.score.toFixed(1)}</div>
                                    </>
                                  ) : (
                                    <span className="text-slate-400 italic">—</span>
                                  )}
                                </td>
                                <td className="py-3 px-3 text-xs">
                                  {largestOpportunity ? (
                                    <>
                                      <div className="font-semibold text-slate-800">{largestOpportunity.navCondition}</div>
                                      <div className="text-rose-700">
                                        −{largestOpportunity.gap.toFixed(1)} vs median
                                      </div>
                                    </>
                                  ) : (
                                    <span className="text-slate-400 italic">At or above median</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* LEI Comparison Tab */}
          <TabsContent value="lei" className="space-y-6">
            {/* Rankings Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="h-5 w-5 text-amber-500" />
                  LEI Overall Rankings
                </CardTitle>
              </CardHeader>
              <CardContent>
                {filteredLeiOrgs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500" data-testid="lei-no-data">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p>No LEI baseline data available for the selected industry.</p>
                    <ClearFiltersLink testId="lei-no-data-clear" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="lei-rankings-table">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-3 font-semibold text-slate-600">Rank</th>
                          <th className="text-left py-3 px-3 font-semibold text-slate-600">Organization</th>
                          <th className="text-center py-3 px-3 font-semibold text-slate-600">Overall</th>
                          {pillarNames.map(p => (
                            <th key={p} className="text-center py-3 px-3 font-semibold text-slate-600" style={{ color: PILLAR_COLORS[p] || '#475569' }}>
                              {p.replace(/^P\d\s+/, '')}
                            </th>
                          ))}
                          <th className="text-center py-3 px-3 font-semibold text-slate-600">Data Points</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLeiOrgs.map((org, i) => (
                          <tr key={org.organization_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`lei-rank-row-${org.organization_id}`}>
                            <td className="py-3 px-3"><RankBadge rank={org.rank} /></td>
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: ORG_COLORS[i % ORG_COLORS.length] }} />
                                {renderOrgLabel(org.organization_id, org.organization_name)}
                              </div>
                            </td>
                            <td className="py-3 px-3 text-center"><ScoreBadge score={org.overall_score} /></td>
                            {pillarNames.map(p => (
                              <td key={p} className="py-3 px-3 text-center">
                                <ScoreBadge score={org.pillars?.[p]?.avg_score ?? null} />
                              </td>
                            ))}
                            <td className="py-3 px-3 text-center text-slate-500">{org.data_points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pillar Bar Chart */}
            {pillarChartData.length > 0 && filteredLeiOrgs.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Score by Pillar</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pillarChartData} barGap={4}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="pillar" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        {filteredLeiOrgs.map((org, i) => (
                          <Bar
                            key={org.organization_id}
                            dataKey={org.organization_name}
                            name={orgLabelById[org.organization_id] || org.organization_name}
                            fill={ORG_COLORS[i % ORG_COLORS.length]}
                            radius={[4, 4, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Measure Detail Cards */}
            {filteredLeiOrgs.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Measure Detail</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(filteredLeiOrgs[0]?.measures || {}).map(([mid, mdata]) => {
                    // Gather all (filtered) orgs' scores for this measure
                    const orgScores = filteredLeiOrgs.map((org, i) => ({
                      id: org.organization_id,
                      name: orgLabelById[org.organization_id] || org.organization_name,
                      score: org.measures?.[mid]?.score,
                      color: ORG_COLORS[i % ORG_COLORS.length]
                    })).filter(o => o.score !== null && o.score !== undefined);

                    const best = orgScores.length > 0 ? orgScores.reduce((a, b) => a.score > b.score ? a : b) : null;

                    return (
                      <Card key={mid} className="text-sm" data-testid={`measure-card-${mid}`}>
                        <CardContent className="py-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-slate-800 truncate" title={mdata.name}>{mdata.name}</span>
                            <Badge variant="outline" className="text-xs" style={{ color: PILLAR_COLORS[mdata.pillar], borderColor: PILLAR_COLORS[mdata.pillar] }}>
                              {mdata.pillar?.replace(/^P\d\s+/, '')}
                            </Badge>
                          </div>
                          <div className="space-y-1.5">
                            {filteredLeiOrgs.map((org, i) => {
                              const s = org.measures?.[mid]?.score;
                              return (
                                <div key={org.organization_id} className="flex items-start justify-between gap-2">
                                  <span className="flex items-start gap-1.5 min-w-0">
                                    <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: ORG_COLORS[i % ORG_COLORS.length] }} />
                                    <span className="min-w-0">
                                      {renderOrgLabel(org.organization_id, org.organization_name)}
                                    </span>
                                  </span>
                                  <span className={`font-mono font-medium shrink-0 ${s !== null && s !== undefined ? (best && s === best.score ? 'text-emerald-600' : 'text-slate-700') : 'text-slate-400'}`}>
                                    {s !== null && s !== undefined ? s.toFixed(1) : '—'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* KPI Comparison Tab */}
          <TabsContent value="kpi" className="space-y-6">
            {filteredKpiOrgs.length === 0 || Object.keys(filteredKpiRankings).length === 0 ? (
              <FilterEmptyState
                onClear={_clearAllFilters}
                hint="No KPI actuals data available for the current filter combination. Try relaxing the Industry, Region, or Company Size filter."
                testId="kpi-filter-empty"
              />
            ) : (
              <>
                {/* KPI Rankings Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(filteredKpiRankings).map(([kid, kdata]) => (
                    <Card key={kid} data-testid={`kpi-card-${kid}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">{kdata.kpi_name}</CardTitle>
                          {kdata.avg !== null && (
                            <Badge variant="outline" className="text-slate-500">
                              Avg: {kdata.avg}
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {(kdata.rankings || []).map((entry, i) => {
                            const isBest = i === 0;
                            const isWorst = i === kdata.rankings.length - 1 && kdata.rankings.length > 1;
                            return (
                              <div key={entry.organization_id} className={`flex items-center justify-between py-2 px-3 rounded-lg ${isBest ? 'bg-emerald-50' : isWorst ? 'bg-rose-50' : 'bg-slate-50'}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                  <RankBadge rank={entry.rank} />
                                  {renderOrgLabel(entry.organization_id, entry.organization_name)}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className={`font-mono font-bold ${isBest ? 'text-emerald-700' : isWorst ? 'text-rose-600' : 'text-slate-700'}`}>
                                    {entry.value?.toLocaleString()}
                                  </span>
                                  {isBest && <ArrowUpRight className="h-4 w-4 text-emerald-500" />}
                                  {isWorst && <ArrowDownRight className="h-4 w-4 text-rose-400" />}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* KPI Bar Chart */}
                {filteredKpiOrgs.length > 0 && (() => {
                  const kpiIds = Object.keys(filteredKpiRankings);
                  const chartData = kpiIds.map(kid => {
                    const entry = { kpi: filteredKpiRankings[kid].kpi_name };
                    filteredKpiOrgs.forEach(org => {
                      entry[org.organization_name] = org.kpis?.[kid]?.value || 0;
                    });
                    return entry;
                  });

                  return (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">KPI Values by Organization</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[350px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} barGap={4}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="kpi" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={60} />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Tooltip />
                              <Legend />
                              {filteredKpiOrgs.map((org, i) => (
                                <Bar
                                  key={org.organization_id}
                                  dataKey={org.organization_name}
                                  name={orgLabelById[org.organization_id] || org.organization_name}
                                  fill={ORG_COLORS[i % ORG_COLORS.length]}
                                  radius={[4, 4, 0, 0]}
                                />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
              </>
            )}
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6" data-testid="trends-tab-content">
            {/* LEI Trend Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-indigo-500" />
                    LEI Score Trends Over Time
                  </CardTitle>
                  <select
                    value={selectedPillar}
                    onChange={(e) => setSelectedPillar(e.target.value)}
                    className="text-sm rounded-lg px-3 py-1.5 border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-indigo-300"
                    data-testid="pillar-trend-selector"
                  >
                    <option value="overall">Overall Average</option>
                    {(leiData?.pillar_names || []).filter(Boolean).map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const orgTrends = filteredOrgTrends;
                  const crossAvg = leiTrends?.cross_org_average || [];
                  const periods = leiTrends?.periods || [];
                  const pillarTrendData = leiTrends?.pillar_trends || {};

                  if (orgTrends.length === 0) {
                    return (
                      <div className="text-center py-8 text-slate-500" data-testid="lei-trends-no-data">
                        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                        <p>No LEI trend data available for the selected industry.</p>
                        <ClearFiltersLink testId="lei-trends-no-data-clear" />
                      </div>
                    );
                  }

                  // Build chart data based on selected pillar. Series
                  // keys use anonymized labels so the legend respects
                  // RBAC scoping.
                  let chartData;
                  if (selectedPillar === 'overall') {
                    chartData = periods.map((p) => {
                      const entry = { period: p };
                      orgTrends.forEach(org => {
                        const pt = org.series.find(s => s.period === p);
                        const label = orgLabelById[org.organization_id] || org.organization_name;
                        entry[label] = pt?.avg_score ?? null;
                      });
                      const avgPt = crossAvg.find(s => s.period === p);
                      entry['Cross-Org Avg'] = avgPt?.avg_score ?? null;
                      return entry;
                    });
                  } else {
                    // Map raw org_name → org_id by matching trends list
                    const nameToId = {};
                    orgTrends.forEach(o => { nameToId[o.organization_name] = o.organization_id; });
                    const pillarData = pillarTrendData[selectedPillar] || {};
                    chartData = periods.map(p => {
                      const entry = { period: p };
                      Object.entries(pillarData).forEach(([orgName, series]) => {
                        const oid = nameToId[orgName];
                        // Honour the page-level industry filter
                        if (oid && !isOrgInFilter(oid)) return;
                        const label = (oid && orgLabelById[oid]) || orgName;
                        const pt = series.find(s => s.period === p);
                        entry[label] = pt?.avg_score ?? null;
                      });
                      return entry;
                    });
                  }

                  const orgNames = selectedPillar === 'overall'
                    ? [...orgTrends.map(o => orgLabelById[o.organization_id] || o.organization_name), 'Cross-Org Avg']
                    : Object.keys(pillarTrendData[selectedPillar] || {})
                        .map(orgName => {
                          const oid = orgTrends.find(o => o.organization_name === orgName)?.organization_id;
                          return oid && isOrgInFilter(oid)
                            ? (orgLabelById[oid] || orgName)
                            : null;
                        })
                        .filter(Boolean);

                  return (
                    <div className="h-[380px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsLineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Legend />
                          {orgNames.map((name, i) => (
                            <Line
                              key={name}
                              type="monotone"
                              dataKey={name}
                              stroke={name === 'Cross-Org Avg' ? '#94a3b8' : ORG_COLORS[i % ORG_COLORS.length]}
                              strokeWidth={name === 'Cross-Org Avg' ? 2 : 2.5}
                              strokeDasharray={name === 'Cross-Org Avg' ? '6 3' : undefined}
                              dot={{ r: 3 }}
                              connectNulls
                            />
                          ))}
                        </RechartsLineChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Period Change Summary Cards */}
            {filteredOrgTrends.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Period Change Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredOrgTrends.map((org, i) => {
                    const avgChangePct = leiTrends?.cross_org_avg_change_pct;
                    const isAboveAvg = org.change_pct !== null && avgChangePct !== null && org.change_pct > avgChangePct;
                    const isYou = org.organization_id === scopedOrgId;
                    const indName = industryForOrg(org.organization_id);
                    const regName = regionForOrg(org.organization_id);
                    const szName = sizeForOrg(org.organization_id);
                    return (
                      <Card
                        key={org.organization_id}
                        className={`border-l-4 ${isYou ? 'ring-2 ring-blue-300' : ''}`}
                        style={{ borderLeftColor: ORG_COLORS[i % ORG_COLORS.length] }}
                        data-testid={`trend-card-${org.organization_id}`}
                      >
                        <CardContent className="py-4">
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <Building2 className="h-4 w-4" style={{ color: ORG_COLORS[i % ORG_COLORS.length] }} />
                            <span className={`font-semibold ${isYou ? 'text-blue-700' : 'text-slate-800'}`}>
                              {orgLabelById[org.organization_id] || org.organization_name}
                            </span>
                            {indName && (
                              <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                                {indName}
                              </span>
                            )}
                            {regName && (
                              <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                                {regName}
                              </span>
                            )}
                            {szName && (
                              <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                {szName}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-slate-50 rounded p-2">
                              <p className="text-slate-500">First Score</p>
                              <p className="font-bold text-slate-800">{org.first_score?.toFixed(1) ?? '—'}</p>
                            </div>
                            <div className="bg-slate-50 rounded p-2">
                              <p className="text-slate-500">Latest Score</p>
                              <p className="font-bold text-slate-800">{org.latest_score?.toFixed(1) ?? '—'}</p>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              {org.trend_direction === 'improving' ? (
                                <TrendingUp className="h-4 w-4 text-emerald-500" />
                              ) : org.trend_direction === 'declining' ? (
                                <TrendingDown className="h-4 w-4 text-rose-500" />
                              ) : (
                                <Minus className="h-4 w-4 text-slate-400" />
                              )}
                              <span className={`font-bold text-sm ${org.trend_direction === 'improving' ? 'text-emerald-600' : org.trend_direction === 'declining' ? 'text-rose-600' : 'text-slate-500'}`}>
                                {org.change_pct !== null ? `${org.change_pct > 0 ? '+' : ''}${org.change_pct}%` : '—'}
                              </span>
                            </div>
                            {avgChangePct !== null && org.change_pct !== null && (
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isAboveAvg ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {isAboveAvg ? 'Above' : 'Below'} avg ({avgChangePct > 0 ? '+' : ''}{avgChangePct}%)
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* KPI Trends Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-teal-500" />
                    KPI Trends Over Time
                  </CardTitle>
                  {Object.keys(kpiTrends?.kpi_trends || {}).length > 0 && (
                    <select
                      value={selectedKpiTrend || ''}
                      onChange={(e) => setSelectedKpiTrend(e.target.value)}
                      className="text-sm rounded-lg px-3 py-1.5 border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-teal-300"
                      data-testid="kpi-trend-selector"
                    >
                      {Object.entries(kpiTrends?.kpi_trends || {}).map(([kid, kdata]) => (
                        <option key={kid} value={kid}>{kdata.kpi_name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const allKpiTrends = kpiTrends?.kpi_trends || {};
                  const periods = kpiTrends?.periods || [];

                  if (Object.keys(allKpiTrends).length === 0 || !selectedKpiTrend || filteredKpiOrgs.length === 0) {
                    return (
                      <div className="text-center py-8 text-slate-500" data-testid="kpi-trends-no-data">
                        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                        <p>{filteredKpiOrgs.length === 0 && _filtersActive
                          ? 'No KPI trend data for the current filter combination.'
                          : 'No KPI trend data available yet.'}</p>
                        <ClearFiltersLink testId="kpi-trends-no-data-clear" />
                      </div>
                    );
                  }

                  const kpiDetail = allKpiTrends[selectedKpiTrend];
                  if (!kpiDetail) return null;

                  const orgLines = kpiDetail.org_lines || {};
                  const crossAvg = kpiDetail.cross_org_average || [];

                  // Map raw org name → org_id via the kpiOrgs list so we
                  // can apply the page-level industry filter and the
                  // RBAC anonymization to the legend keys.
                  const nameToId = {};
                  (kpiData?.organizations || []).forEach(o => {
                    nameToId[o.organization_name] = o.organization_id;
                  });
                  const labelByOrgName = (orgName) => {
                    const oid = nameToId[orgName];
                    if (oid && !isOrgInFilter(oid)) return null;
                    return (oid && orgLabelById[oid]) || orgName;
                  };

                  const chartData = periods.map(p => {
                    const entry = { period: p };
                    Object.entries(orgLines).forEach(([orgName, data]) => {
                      const label = labelByOrgName(orgName);
                      if (!label) return;
                      const pt = data.series.find(s => s.period === p);
                      entry[label] = pt?.value ?? null;
                    });
                    const avgPt = crossAvg.find(s => s.period === p);
                    entry['Cross-Org Avg'] = avgPt?.value ?? null;
                    return entry;
                  });

                  const lineNames = [
                    ...Object.keys(orgLines).map(labelByOrgName).filter(Boolean),
                    'Cross-Org Avg',
                  ];

                  return (
                    <>
                      <div className="h-[350px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsLineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Legend />
                            {lineNames.map((name, i) => (
                              <Line
                                key={name}
                                type="monotone"
                                dataKey={name}
                                stroke={name === 'Cross-Org Avg' ? '#94a3b8' : ORG_COLORS[i % ORG_COLORS.length]}
                                strokeWidth={name === 'Cross-Org Avg' ? 2 : 2.5}
                                strokeDasharray={name === 'Cross-Org Avg' ? '6 3' : undefined}
                                dot={{ r: 3 }}
                                connectNulls
                              />
                            ))}
                          </RechartsLineChart>
                        </ResponsiveContainer>
                      </div>
                      {/* KPI change summary — must respect the same
                          anonymization + industry/region/size filter
                          that scopes the line chart above. */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.entries(orgLines)
                          .map(([orgName, data]) => {
                            const oid = nameToId[orgName];
                            const label = labelByOrgName(orgName);
                            return label ? { oid, orgName, label, data } : null;
                          })
                          .filter(Boolean)
                          .map((entry, i) => {
                            const isYou = entry.oid && entry.oid === scopedOrgId;
                            return (
                              <div
                                key={entry.orgName}
                                className={`flex items-center justify-between bg-slate-50 rounded-lg p-3 text-sm ${isYou ? 'ring-2 ring-blue-200' : ''}`}
                                data-testid={`kpi-trend-summary-${entry.oid || entry.orgName}`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: ORG_COLORS[i % ORG_COLORS.length] }} />
                                  {entry.oid
                                    ? renderOrgLabel(entry.oid, entry.orgName)
                                    : <span className="font-medium text-slate-700">{entry.label}</span>
                                  }
                                </div>
                                <span className={`font-bold shrink-0 ${entry.data.trend_direction === 'improving' ? 'text-emerald-600' : entry.data.trend_direction === 'declining' ? 'text-rose-600' : 'text-slate-500'}`}>
                                  {entry.data.change_pct !== null ? `${entry.data.change_pct > 0 ? '+' : ''}${entry.data.change_pct}%` : '—'}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>

            {/* AI-Powered Insights */}
            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-violet-50/50" data-testid="ai-insights-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-indigo-500" />
                    AI-Powered Cross-Org Insights
                  </CardTitle>
                  <button
                    onClick={generateInsights}
                    disabled={insightsLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="generate-insights-btn"
                  >
                    {insightsLoading ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Sparkles className="h-4 w-4" /> {aiInsights ? 'Refresh Insights' : 'Generate Insights'}</>
                    )}
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {!aiInsights && !insightsLoading && (
                  <div className="text-center py-8 text-slate-500" data-testid="insights-empty">
                    <Sparkles className="h-10 w-10 mx-auto mb-3 text-indigo-300" />
                    <p className="font-medium text-slate-700 mb-1">Generate AI-powered insights</p>
                    <p className="text-sm text-slate-500 max-w-md mx-auto">
                      Click the button above to analyze cross-org performance data and get executive-ready insights with actionable recommendations.
                    </p>
                  </div>
                )}
                {insightsLoading && (
                  <div className="text-center py-8" data-testid="insights-loading">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mx-auto mb-3" />
                    <p className="text-sm text-slate-600">Analyzing performance data across organizations...</p>
                  </div>
                )}
                {aiInsights && !insightsLoading && (
                  <div data-testid="insights-content">
                    <div className="prose prose-sm prose-slate max-w-none [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-slate-800 [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-slate-700 [&_ul]:space-y-1 [&_li]:text-slate-600 [&_strong]:text-slate-800">
                      {(() => {
                        // Helper: render a line, converting **runs**
                        // of markdown bold to <strong>. Splitting on
                        // `**` and bolding every odd-indexed segment
                        // is robust to multiple bold runs per line
                        // and avoids the regex backtracking edge cases
                        // we'd hit with `replace(/\*\*(.+?)\*\*/g)`.
                        const renderInline = (text, kp) => {
                          const parts = text.split('**');
                          return parts.map((part, j) =>
                            j % 2 === 1
                              ? <strong key={`${kp}-b${j}`} className="text-slate-800">{part}</strong>
                              : <React.Fragment key={`${kp}-t${j}`}>{part}</React.Fragment>
                          );
                        };
                        // Detect lines that are entirely a bold heading
                        // like `**Key Insights**:` or `**Top Performers**`
                        // — the model uses these instead of `##`. Treat
                        // them as h3.
                        const isInlineHeading = (line) => {
                          const t = line.trim();
                          return /^\*\*[^*]+\*\*:?$/.test(t);
                        };
                        return anonymizeNarrative(aiInsights.insights).split('\n').map((line, i) => {
                          const lineKey = `insight-${i}-${line.slice(0, 15).replace(/\s/g, '')}`;
                          if (line.startsWith('## ')) return <h2 key={lineKey}>{renderInline(line.replace('## ', ''), lineKey)}</h2>;
                          if (line.startsWith('### ')) return <h3 key={lineKey}>{renderInline(line.replace('### ', ''), lineKey)}</h3>;
                          if (isInlineHeading(line)) {
                            const t = line.trim().replace(/^\*\*/, '').replace(/\*\*:?$/, '');
                            return <h3 key={lineKey}>{t}</h3>;
                          }
                          if (line.startsWith('- ') || line.startsWith('* ')) {
                            const body = line.replace(/^[-*]\s*/, '');
                            return (
                              <div key={lineKey} className="flex items-start gap-2 py-0.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 flex-shrink-0" />
                                <p className="text-slate-600 text-sm leading-relaxed">
                                  {renderInline(body, lineKey)}
                                </p>
                              </div>
                            );
                          }
                          if (line.trim() === '') return <div key={lineKey} className="h-2" />;
                          return (
                            <p key={lineKey} className="text-sm text-slate-600 leading-relaxed">
                              {renderInline(line, lineKey)}
                            </p>
                          );
                        });
                      })()}
                    </div>
                    <div className="mt-4 pt-3 border-t border-indigo-100 flex items-center justify-between text-xs text-slate-400">
                      <span>Generated {new Date(aiInsights.generated_at).toLocaleString()}</span>
                      <span>{aiInsights.org_count} organizations analyzed</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
    </div>
    </PageShell>
  );
}
